import { selectCurrentYearInvestmentQuota } from "@/application/mappers/investments/investmentFormMapper";
import { useBeneficiaryDetail } from "@/application/queries";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { useCallback, useEffect, useRef, useState } from "react";
import type { UseInvestmentFormSyncProps } from "@/application/workflows/investments/investmentFormSync.types";

export type { UseInvestmentFormSyncProps };

export function useInvestmentFormSyncEffects(props: UseInvestmentFormSyncProps) {
  const {
    isOpen,
    isEditMode,
    preselectedProject,
    preselectedInvestor,
    preselectedState,
    existingInvestment,
    formData,
    updateFormData,
    setSelectedInvestor,
    selectedQuota,
    setSelectedQuota,
    setFormData,
  } = props;

  const orgId = useOrganizationStore((state) => state.selectedOrganizationUuid ?? "");
  const hasPrepopulated = useRef(false);
  const lastAutoFilledQuotaUuid = useRef<string | null>(null);
  const [beneficiaryUuidToFetch, setBeneficiaryUuidToFetch] = useState<string | null>(null);
  const [shouldAutoSelectQuota, setShouldAutoSelectQuota] = useState(true);

  const beneficiaryQuery = useBeneficiaryDetail(orgId, beneficiaryUuidToFetch ?? undefined);

  const beneficiaryDetails = beneficiaryQuery.data
    ? {
        fiscal_quotas: beneficiaryQuery.data.fiscal_quotas || [],
        incentive_type: beneficiaryQuery.data.beneficiary?.incentive_type ?? null,
      }
    : null;
  const loadingBeneficiaryDetails = beneficiaryQuery.isFetching;

  const lastProcessedBeneficiaryDataRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      beneficiaryQuery.data &&
      shouldAutoSelectQuota &&
      beneficiaryUuidToFetch &&
      lastProcessedBeneficiaryDataRef.current !== beneficiaryUuidToFetch
    ) {
      lastProcessedBeneficiaryDataRef.current = beneficiaryUuidToFetch;
      const currentYear = new Date().getFullYear();
      const currentYearQuota = selectCurrentYearInvestmentQuota(
        beneficiaryQuery.data.fiscal_quotas,
        currentYear,
      );
      if (currentYearQuota) updateFormData({ quotaId: currentYearQuota.uuid });
    }
  }, [beneficiaryQuery.data, shouldAutoSelectQuota, beneficiaryUuidToFetch, updateFormData]);

  const loadBeneficiaryDetails = useCallback((beneficiaryUuid: string, autoSelectQuota = true) => {
    lastProcessedBeneficiaryDataRef.current = null;
    setShouldAutoSelectQuota(autoSelectQuota);
    setBeneficiaryUuidToFetch(beneficiaryUuid);
  }, []);

  return {
    loadingBeneficiaryDetails,
    beneficiaryDetails,
    detailError: beneficiaryQuery.error,
    loadBeneficiaryDetails,
    hasPrepopulated,
    lastAutoFilledQuotaUuid,
    lastProcessedBeneficiaryDataRef,
    // Re-export props needed by field watchers
    isOpen,
    isEditMode,
    preselectedProject,
    preselectedInvestor,
    preselectedState,
    existingInvestment,
    formData,
    updateFormData,
    setSelectedInvestor,
    availableFiscalQuotas: props.availableFiscalQuotas,
    selectedQuota,
    setSelectedQuota,
    setFormData,
  };
}
